import { LinearGradient } from 'expo-linear-gradient';
import { Search, X } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  StyleSheet,
  type TextInput,
  View,
  type ViewStyle,
} from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { FormInput } from './FormInput';

interface SearchInputProps {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  loading?: boolean;
  onClear?: () => void;
  autoFocus?: boolean;
  onBlur?: () => void;
  style?: ViewStyle;
  // Compact variant for tight chrome (e.g. the dashboard header): shorter field
  // and no heavy drop-shadow glow, so the focus state stays inside its bounds.
  compact?: boolean;
  testID?: string;
}

const ICON_SIZE = 18;

function getWebShadow(focused: boolean, hovered: boolean, compact: boolean): ViewStyle {
  if (compact) {
    // Single teal border on focus (no drop-shadow that would bleed past chrome).
    return { boxShadow: 'none' } as unknown as ViewStyle;
  }
  if (focused) {
    return {
      boxShadow: `0 12px 28px ${colors.glow.cyan}`,
    } as unknown as ViewStyle;
  }
  if (hovered) {
    return { boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)' } as unknown as ViewStyle;
  }
  return { boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)' } as unknown as ViewStyle;
}

function getIconColor(focused: boolean, hovered: boolean): string {
  if (focused) return colors.primary.main;
  if (hovered) return colors.text.secondary;
  return colors.text.tertiary;
}

function createAnimatedContainerStyle(focusAnim: Animated.Value, compact: boolean) {
  return {
    borderColor: focusAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [colors.border.input, colors.primary.main],
    }),
    backgroundColor: focusAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [colors.background.input, colors.background.elevated],
    }),
    transform: [
      {
        // The compact field lives inside an overflow-clipped header chrome;
        // a focus lift would push the top border out of the clip. Skip it.
        translateY: focusAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [0, compact ? 0 : -1],
        }),
      },
    ],
  };
}

function createIconScale(focusAnim: Animated.Value) {
  return focusAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.08],
  });
}

function createGlowOpacity(focusAnim: Animated.Value) {
  return focusAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });
}

function buildTestID(base: string | undefined, suffix: string): string | undefined {
  return base ? `${base}-${suffix}` : undefined;
}

export function SearchInput({
  value,
  onChangeText,
  placeholder = 'Search...',
  loading = false,
  onClear,
  autoFocus = false,
  onBlur,
  style,
  compact = false,
  testID,
}: Readonly<SearchInputProps>) {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<TextInput>(null);
  const focusAnim = useRef(new Animated.Value(0)).current;
  const hoverAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(focusAnim, {
      toValue: focused ? 1 : 0,
      duration: 220,
      useNativeDriver: false,
    }).start();
  }, [focusAnim, focused]);

  useEffect(() => {
    Animated.timing(hoverAnim, {
      toValue: hovered ? 1 : 0,
      duration: 180,
      useNativeDriver: false,
    }).start();
  }, [hoverAnim, hovered]);

  const handleClear = () => {
    onChangeText('');
    onClear?.();
    inputRef.current?.focus();
  };

  const showClearButton = value.length > 0;
  const isWeb = Platform.OS === 'web';

  const animatedContainerStyle = createAnimatedContainerStyle(focusAnim, compact);
  const iconScale = createIconScale(focusAnim);
  const iconColor = getIconColor(focused, hovered);
  const webShadowStyle = isWeb ? getWebShadow(focused, hovered, compact) : {};
  const handlePointerEnter = isWeb ? () => setHovered(true) : undefined;
  const handlePointerLeave = isWeb ? () => setHovered(false) : undefined;
  const loadingTestID = buildTestID(testID, 'loading');
  const inputTestID = buildTestID(testID, 'input');
  const clearTestID = buildTestID(testID, 'clear');

  return (
    <View
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      style={styles.wrapper}
    >
      <Animated.View
        style={[
          styles.container,
          compact && styles.containerCompact,
          style,
          animatedContainerStyle,
          webShadowStyle,
        ]}
      >
        {!compact && (
          <Animated.View
            pointerEvents="none"
            style={[styles.glowLayer, { opacity: createGlowOpacity(focusAnim) }]}
          >
            <LinearGradient
              colors={colors.gradients.activeWash}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
          </Animated.View>
        )}

        <Animated.View style={[styles.iconContainer, { transform: [{ scale: iconScale }] }]}>
          {loading ? (
            <ActivityIndicator size="small" color={iconColor} testID={loadingTestID} />
          ) : (
            <Search size={ICON_SIZE} color={iconColor} />
          )}
        </Animated.View>

        <FormInput
          ref={inputRef}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          style={styles.input}
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="search"
          testID={inputTestID}
        />

        {showClearButton && (
          <Pressable
            onPress={handleClear}
            style={({ pressed }) => [styles.clearButton, pressed && styles.clearButtonPressed]}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            testID={clearTestID}
          >
            <X size={ICON_SIZE} color={colors.text.tertiary} />
          </Pressable>
        )}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: tokens.radius.lg,
    paddingHorizontal: tokens.spacing.md,
    height: 50,
    overflow: 'hidden',
    position: 'relative',
  },
  containerCompact: {
    height: 40,
  },
  glowLayer: {
    ...StyleSheet.absoluteFillObject,
  },
  iconContainer: {
    width: ICON_SIZE + 8,
    height: ICON_SIZE + 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: tokens.spacing.sm,
  },
  input: {
    flex: 1,
    fontSize: tokens.fontSize.base,
    color: colors.text.primary,
    paddingVertical: 0,
  },
  clearButton: {
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.full,
    marginLeft: tokens.spacing.xs,
  },
  clearButtonPressed: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
});
